const ProductShopSelector = require("../../../../src/includes/shops/ProductShopSelector").default;
const React = require("react");
// react-bootstrap ships an ESM build picked over the CJS one by webpack --
// plain require() then returns the raw {default, __esModule} namespace
// instead of the component itself (unlike `import`, which babel auto-unwraps
// -- see the identical note in CJDropshippingViewModal.js).
const interopDefault = (mod) => (mod && mod.default) ? mod.default : mod;
const Modal = interopDefault(require("react-bootstrap/Modal"));
const { useCJDropshipping } = require("../../context/CJDropshippingContext");
const { useDropshippingResource } = require("../../hooks/useDropshippingResource");
const { Button, ResourceState } = require("../ui");
const { CJSyncStepper } = require("./CJSyncStepper");
const { CJCategoryMappingStep } = require("./CJCategoryMappingStep");
const { CJShippingPricingStep } = require("./CJShippingPricingStep");
const { CJSynchronizationStep } = require("./CJSynchronizationStep");
const { CJSyncActionModal } = require("../CJSyncActionModal");
const { getCategoriesByID } = require("../../../../src/includes/auth/apiCategory.js");
const { PASSWORD_CONFIRMATION_ACTIONS } = require("../../../../src/includes/pageInside/sync/passwordConfirmation.js");
const h = React.createElement;

// `groups` = [{cjCategory, elakayCategories: [{catID, gategoriesLink, name}]}]
// (voir CJCategoryMappingStep -- un groupe par niveau CJ coché). Aplati vers
// la vraie forme Product.categoriesID (CategoryLinkSchema : {catID: ObjectId,
// gategoriesLink: []} uniquement -- voir server_elakay/elakay_models/product.js),
// dédupliqué par catID (la même catégorie Elakay peut être liée sous
// plusieurs niveaux CJ, mais ne doit apparaître qu'une fois dans le
// categoriesID final). `name` reste une commodité d'affichage côté client,
// jamais envoyée : le schema n'a pas ce champ.
const buildCategoriesIDPayload = (groups) => {
  const byId = new Map();
  for (const group of groups || []) {
    for (const entry of group.elakayCategories || []) {
      if (!entry?.catID) continue;
      byId.set(String(entry.catID), { catID: entry.catID, gategoriesLink: entry.gategoriesLink || [] });
    }
  }
  return [...byId.values()];
};

// Grand modal 3 étapes, ouvert par les boutons "Add" (mode="import", produit
// CJ pas encore importé) ET "Sync" (mode="sync", déjà importé -- elakayProductId
// garanti non-null, voir ui.js ProductCard) sur un même produit CJ. N'a
// jamais vocation à s'ouvrir pour un produit natif Elakay : les deux seuls
// points d'entrée sont ces boutons, tous deux masqués pour tout ce qui n'est
// pas sourceType==="dropshipping"/platformSlug==="cj".
//
// Règle centrale : AUCUN appel réseau qui crée/modifie le produit Elakay
// avant "Finalize Synchronization" (finalizeCJProductSynchronization
// ci-dessous est la SEULE fonction autorisée à le faire). Chaque étape ne
// fait que lire/accumuler de l'état local (categoriesValue, etc.) contrôlé
// par ce composant -- voir CJCategoryMappingStep, qui ne persiste plus rien
// lui-même (au contraire de clickSubCat/linkCTP dans EditProduct, où un
// produit existe déjà).
//
// Les 3 étapes ("Categories", "Shipping & Pricing", "Synchronization") sont
// toutes fonctionnelles. "Synchronization" (CJSynchronizationStep) ne
// persiste jamais rien elle-même -- elle affiche une simulation en lecture
// seule du produit final (mêmes clés que elakay_models/product.js) à partir
// de categoriesValue (étape 1) + pricingResult (étape 2), et ne remonte que
// le choix `autoSync` (seul réglage réellement appliqué à Finalize depuis
// cette étape, voir finalizeCJProductSynchronization ci-dessous).
// `inline` (défaut false) : rend le même contenu (stepper + 3 étapes +
// footer Previous/Next/Finalize) SANS le wrapper react-bootstrap <Modal>
// (pas de backdrop, pas de bouton de fermeture, pas de centrage/dialog) --
// utilisé par editProduct.jsx (onglet Synchronization, intégré directement
// dans la page plutôt qu'en popup). `show` est ignoré en mode inline
// (toujours actif dès le montage, pas de notion d'ouverture/fermeture) et
// `onHide` y devient optionnel (rien à fermer). Toute la logique (hooks,
// effects, finalizeCJProductSynchronization) reste identique dans les deux
// cas -- seul le wrapper JSX change, pour ne jamais faire diverger le
// comportement entre le modal du catalogue CJ et cette version intégrée.
const CJSyncModal = ({ show, onHide = () => {}, mode = "sync", dropshippingProductId, elakayProductId, onSynced, inline = false, sellerAddress = null }) => {
  const { api, t, userId, isCJConnected } = useCJDropshipping();
  const effectiveShow = inline ? true : show;
  const [shopIds, setShopIds] = React.useState(undefined);
  const [activeStep, setActiveStep] = React.useState(0);
  const [maxUnlockedStep, setMaxUnlockedStep] = React.useState(0);
  const [step1Valid, setStep1Valid] = React.useState(false);
  const [step2Valid, setStep2Valid] = React.useState(false);
  const [step3Valid, setStep3Valid] = React.useState(false);
  const [categoriesValue, setCategoriesValue] = React.useState([]);
  const [pricingResult, setPricingResult] = React.useState(null); // payload prêt de CJShippingPricingStep, voir finalizeCJProductSynchronization
  const [syncResult, setSyncResult] = React.useState(null); // { autoSync }, prêt de CJSynchronizationStep
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [finalizeError, setFinalizeError] = React.useState("");
  const [showPasswordModal, setShowPasswordModal] = React.useState(false);
  const [passwordError, setPasswordError] = React.useState("");
  const finalizeLockRef = React.useRef(false);

  const productResource = useDropshippingResource(
    () => (effectiveShow && dropshippingProductId) ? api.getProduct(dropshippingProductId) : Promise.resolve(null),
    [api, effectiveShow, dropshippingProductId]
  );
  const cjProduct = productResource.data?.product || null;

  // mode="import" : aucun produit Elakay n'existe encore, donc rien à
  // comparer/pré-remplir -- categoriesValue démarre vide (voir reset ci-dessous).
  const compareResource = useDropshippingResource(
    () => (effectiveShow && mode === "sync" && elakayProductId) ? api.compareCJProduct(elakayProductId) : Promise.resolve(null),
    [api, effectiveShow, mode, elakayProductId]
  );

  // Pré-remplit la sélection (mode="sync" seulement) avec le mapping déjà
  // enregistré (categoriesID), résolu en {catID, gategoriesLink, name} via un
  // fetch par catID. categoriesID (champ Product plat) ne garde aucune trace
  // de sous quel niveau CJ chaque catégorie avait été liée à l'origine --
  // reconstruction impossible avec certitude, donc tout est regroupé sous un
  // seul groupe synthétique portant le niveau CJ le plus profond (le plus
  // spécifique) du produit, une seule fois par ouverture.
  const prefillDoneRef = React.useRef(false);
  React.useEffect(() => {
    if (!effectiveShow) return;
    setShopIds(undefined);
    setCategoriesValue([]);
    prefillDoneRef.current = mode !== "sync"; // rien à préremplir en mode import
  }, [effectiveShow, mode, elakayProductId]);

  React.useEffect(() => {
    if (prefillDoneRef.current) return;
    const association = compareResource.data;
    if (association && shopIds === undefined && association.boutiqueId?.length) setShopIds(association.boutiqueId.map(String));
    const existing = compareResource.data?.categoriesID;
    if (!existing) return;
    // Attend que productResource (cjProduct) ait fini de se charger avant de
    // marquer le préremplissage "fait" -- sinon, si compareResource résout
    // avant productResource, leafLevel serait toujours vide et le groupe
    // synthétique ne serait jamais construit malgré un cjProduct valide
    // arrivant juste après (course entre les deux ressources).
    if (productResource.loading) return;
    prefillDoneRef.current = true;
    if (!existing.length) return;
    const cjCat = cjProduct?.category;
    const leafLevel = Array.isArray(cjCat?.path) && cjCat.path.length ? cjCat.path[cjCat.path.length - 1] : cjCat;
    if (!leafLevel?.name) return; // pas de catégorie CJ connue -- rien à rattacher, le seller repart de zéro à l'étape 1
    let cancelled = false;
    Promise.all(existing.map((entry) => getCategoriesByID(entry.catID).catch(() => null)))
      .then((results) => {
        if (cancelled) return;
        const elakayCategories = results.map((data, index) => data?.cartegory
          ? { catID: data.cartegory._id, gategoriesLink: data.cartegory.categoryID, name: data.cartegory.name }
          : { catID: existing[index].catID, gategoriesLink: existing[index].gategoriesLink || [], name: [t("h_cjwiz_cat_unresolved", "(category)")] });
        const externalCategoryId = leafLevel.id || leafLevel.externalCategoryId || "";
        setCategoriesValue([{
          cjCategory: {
            // Groupe synthétique (voir commentaire ci-dessus) : la clé n'a
            // pas besoin de matcher un `level.key` de buildCJLevels puisque
            // ce groupe n'est jamais recoché/décoché depuis la liste des
            // niveaux -- seule l'unicité de cette clé compte ici.
            key: externalCategoryId || leafLevel.name || "prefilled",
            externalCategoryId,
            name: leafLevel.name,
            fullPath: leafLevel.name,
          },
          elakayCategories,
        }]);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareResource.data, cjProduct, productResource.loading]);

  React.useEffect(() => {
    if (!effectiveShow) {
      setActiveStep(0);
      setMaxUnlockedStep(0);
      setStep2Valid(false);
      setStep3Valid(false);
      setPricingResult(null);
      setSyncResult(null);
      setFinalizeError("");
      setShowPasswordModal(false);
      setPasswordError("");
      finalizeLockRef.current = false;
    }
  }, [effectiveShow]);

  React.useEffect(() => {
    setMaxUnlockedStep((current) => (step1Valid ? Math.max(current, 1) : current));
  }, [step1Valid]);

  React.useEffect(() => {
    setMaxUnlockedStep((current) => (step2Valid ? Math.max(current, 2) : current));
  }, [step2Valid]);

  const steps = [
    { id: "categories", label: t("h_cjwiz_step_categories", "Categories") },
    { id: "shipping", label: t("h_cjwiz_step_shipping", "Shipping & Pricing") },
    { id: "sync", label: t("h_cjwiz_step_sync", "Synchronization") },
  ];

  const handleNext = () => setActiveStep((step) => Math.min(step + 1, steps.length - 1));
  const handlePrevious = () => setActiveStep((step) => Math.max(0, step - 1));

  const canFinalize = step1Valid && step2Valid && step3Valid && !isSubmitting;

  // Seule fonction autorisée à créer/modifier le produit Elakay -- voir
  // point 10 de la demande (categoriesID/shipping/pricing arrivent tous ici ;
  // filterProduct/variants/options de sync suivent avec l'étape 3).
  const finalizeCJProductSynchronization = async (confirmationToken) => {
    if (isSubmitting || finalizeLockRef.current || !confirmationToken) return;
    const confirmation = { confirmationToken, confirmationAction: PASSWORD_CONFIRMATION_ACTIONS.CJ_SYNC_FINALIZE };
    finalizeLockRef.current = true;
    setIsSubmitting(true);
    setFinalizeError("");
    try {
      const categoriesID = buildCategoriesIDPayload(categoriesValue);
      // `shippingMode` reste une commodité d'affichage locale (déjà traduite
      // en `shipping: boolean` ci-dessous) -- pas un champ Product, jamais envoyée.
      // `variantSelection` (ONE global selection, same for every destination
      // country) is applied by the sync call, never sent to updateCJPricing.
      const { shippingMode, variantSelection, ...pricingPayload } = pricingResult || {};
      const syncOptions = variantSelection ? { variantSelection } : {};
      if (mode === "sync") {
        const data = await api.updateElakayProduct(elakayProductId, { categoriesID, ...(shopIds !== undefined ? { boutiqueId: shopIds } : {}) }, confirmation);
        if (data?.error) throw new Error(data.error);
        // POST .../cj/products/:id/sync -- applique aussi les dernières
        // valeurs CJ (nom/images/etc., sous réserve des syncLocks déjà en
        // place) AVANT le prix calculé ci-dessous, pour que ce dernier reste
        // le mot de la fin (un sync qui suivrait pourrait sinon écraser le
        // prix choisi par le seller si "price" n'est pas verrouillé).
        const synced = await api.syncCJProductByElakayId(elakayProductId, confirmation, syncOptions);
        if (synced?.error) throw new Error(synced.error);
        if (pricingResult) {
          const priced = await api.updateCJPricing(elakayProductId, pricingPayload, confirmation);
          if (priced?.error) throw new Error(priced.error);
        }
        if (syncResult) {
          const autoSynced = await api.updateAutoSync(elakayProductId, false, confirmation);
          if (autoSynced?.error) throw new Error(autoSynced.error);
          const autoPaid = await api.updateAutoPayOverride(elakayProductId, Boolean(syncResult.autoPayOverride), confirmation);
          if (autoPaid?.error) throw new Error(autoPaid.error);
        }
      } else {
        if (!cjProduct?.externalProductId) throw new Error(t("h_cjwiz_finalize_missing_product", "Missing CJ product reference."));
        if (!userId) throw new Error(t("h_cjwiz_finalize_missing_owner", "Missing Elakay product owner."));
        const imported = await api.importCJProduct(cjProduct.externalProductId, {
          boutiqueId: shopIds,
          owner: userId,
          sellerId: userId,
          ...syncOptions,
        }, confirmation);
        if (imported?.error) throw new Error(imported.error);
        const newProductId = imported?.product?._id;
        if (newProductId && categoriesID.length) {
          const data = await api.updateElakayProduct(newProductId, { categoriesID }, confirmation);
          if (data?.error) throw new Error(data.error);
          // Un import seul ne construit jamais filterProduct (aucune
          // catégorie encore liée à ce moment-là côté serveur, voir
          // normalizeCJProductToElakayProduct) -- categoriesID vient tout
          // juste d'être posé ci-dessus, ce sync le déclenche immédiatement
          // au lieu d'attendre le prochain cron (jusqu'à 6h) pour que le
          // produit devienne visible côté client (filterProduct non vide est
          // une condition requise par toutes les requêtes produit client).
          const synced = await api.syncCJProductByElakayId(newProductId, confirmation, syncOptions);
          if (synced?.error) throw new Error(synced.error);
        }
        if (newProductId && pricingResult) {
          const priced = await api.updateCJPricing(newProductId, pricingPayload, confirmation);
          if (priced?.error) throw new Error(priced.error);
        }
        if (newProductId && syncResult) {
          const autoSynced = await api.updateAutoSync(newProductId, false, confirmation);
          if (autoSynced?.error) throw new Error(autoSynced.error);
          const autoPaid = await api.updateAutoPayOverride(newProductId, Boolean(syncResult.autoPayOverride), confirmation);
          if (autoPaid?.error) throw new Error(autoPaid.error);
        }
      }
      onSynced && onSynced();
      setShowPasswordModal(false);
      onHide && onHide();
    } catch (error) {
      if (error?.status === 403 || error?.status === 428 || error?.code === "DROPSHIPPING_PASSWORD_INVALID") {
        setPasswordError(error.message || t("h_cjwiz_password_invalid", "Mot de passe Elakay incorrect."));
      } else {
        setPasswordError(error.message);
      }
    } finally {
      setIsSubmitting(false);
      finalizeLockRef.current = false;
    }
  };

  const openFinalizePasswordModal = () => {
    if (!canFinalize) return;
    setPasswordError("");
    setShowPasswordModal(true);
  };

  const closeFinalizePasswordModal = () => {
    if (isSubmitting) return;
    setPasswordError("");
    setShowPasswordModal(false);
  };

  const image = cjProduct?.images?.[0] || "https://placehold.co/64x64/e7f4f6/007399?text=CJ";
  const loading = productResource.loading || (mode === "sync" && compareResource.loading);
  const cjCategory = cjProduct?.category || null;

  // Header/Body/Footer construits une seule fois, réutilisés tels quels par
  // les deux wrappers ci-dessous (Modal réel vs <div> inline) -- jamais deux
  // rendus divergents du même contenu.
  const headerContent = h("div", { className: "cjwiz-modal__identity" },
    h("img", { className: "cjwiz-modal__thumb", src: image, alt: "" }),
    h("div", null,
      h(inline ? "h5" : Modal.Title, inline ? { className: "modal-title" } : null,
        cjProduct?.title || t("h_cjwiz_title", "Synchronize product")),
      h("div", { className: "cjwiz-modal__meta" },
        cjProduct?.sku ? h("span", null, `SKU: ${cjProduct.sku}`) : null,
        h("span", { className: `cjwiz-badge ${isCJConnected ? "cjwiz-badge--success" : "cjwiz-badge--error"}` },
          isCJConnected ? t("h_cjwiz_cj_connected", "CJ connected") : t("h_cjwiz_cj_disconnected", "CJ disconnected")),
        h("span", { className: "cjwiz-badge cjwiz-badge--muted" },
          mode === "import" ? t("h_cjwiz_mode_import", "New import") : t("h_cjwiz_mode_sync", "Already imported"))
      )
    )
  );

  const bodyContent = h(React.Fragment, null,
    h(ProductShopSelector, { value: shopIds, onChange: setShopIds }),
    h(CJSyncStepper, { steps, activeStep, maxUnlockedStep, onSelect: setActiveStep }),

    h(ResourceState, {
      loading, error: productResource.error || compareResource.error,
      empty: !cjProduct, onRetry: () => { productResource.reload(); mode === "sync" && compareResource.reload(); }, t,
    },
      // Les 3 étapes restent montées EN PERMANENCE une fois chargées (masquées
      // via display:none plutôt que retirées par un ternaire) -- avec un
      // ternaire, changer d'étape démonte le composant qui n'est plus actif :
      // CJShippingPricingStep/ShippingCostCalculator perdrait tout son état
      // interne (shippingMode choisi, purchasePrice édité, prix cible/profit
      // visé, remise, devis CJ déjà obtenu) et rechargerait un NOUVEAU devis
      // de transport CJ à chaque retour sur l'étape, jusqu'à Finalize
      // Synchronization. Rester monté garde cet état identique du premier
      // chargement jusqu'au clic final, sans nouvel appel réseau.
      h("div", { key: "step-categories", style: { display: activeStep === 0 ? "block" : "none" } },
        h(CJCategoryMappingStep, {
          cjCategory,
          value: categoriesValue,
          onChange: setCategoriesValue,
          onValidChange: setStep1Valid,
          t,
        })
      ),
      h("div", { key: "step-shipping", style: { display: activeStep === 1 ? "block" : "none" } },
        h(CJShippingPricingStep, {
          dropshippingProductId,
          costPrice: cjProduct?.costPrice,
          // Resync: pre-fill countries / variants / country prices from the
          // product's saved configuration.
          initialConfig: mode === "sync" ? compareResource.data?.dropshippingData || null : null,
          onValidChange: setStep2Valid,
          onPricingResult: setPricingResult,
          t,
        })
      ),
      h("div", { key: "step-sync", style: { display: activeStep === 2 ? "block" : "none" } },
        h(CJSynchronizationStep, {
          mode,
          cjProduct,
          compareData: mode === "sync" ? compareResource.data : null,
          categoriesValue,
          pricingResult,
          ownerId: userId,
          sellerAddress,
          onValidChange: setStep3Valid,
          onSyncResult: setSyncResult,
          t,
        })
      )
    ),
    finalizeError ? h("div", { className: "cjds-alert", role: "alert" }, finalizeError) : null
  );

  const footerContent = h(React.Fragment, null,
    h(Button, { variant: "secondary", disabled: activeStep === 0, onClick: handlePrevious },
      t("h_cjwiz_previous", "Previous")),
    activeStep < steps.length - 1
      ? h(Button, { disabled: ![step1Valid, step2Valid, step3Valid][activeStep], onClick: handleNext },
          t("h_cjwiz_next", "Next"))
      : h(Button, {
          disabled: !canFinalize,
          title: !canFinalize ? t("h_cjwiz_finalize_soon", "Complete all steps before finalizing") : "",
          onClick: openFinalizePasswordModal,
        }, isSubmitting ? t("h_cjwiz_finalizing", "Finalizing...") : t("h_cjwiz_finalize", "Finalize Sync"))
  );

  const passwordModal = h(CJSyncActionModal, {
    show: showPasswordModal,
    onHide: closeFinalizePasswordModal,
    mode: "finalize",
    title: t("h_cjwiz_password_title", "Finalize CJ synchronization"),
    subtitle: t("h_cjwiz_password_desc", "Review and confirm the final write to the Elakay product."),
    cjProduct,
    loading: isSubmitting,
    error: passwordError,
    passwordRequired: true,
    confirmationAction: PASSWORD_CONFIRMATION_ACTIONS.CJ_SYNC_FINALIZE,
    onPasswordConfirm: ({ password, action }) => api.confirmPassword(password, action),
    confirmLabel: t("h_cjwiz_password_confirm", "Confirm and finalize"),
    cancelLabel: t("h_cjwiz_cancel", "Cancel"),
    onConfirm: ({ confirmationToken }) => finalizeCJProductSynchronization(confirmationToken),
  },
    h("div", { className: "cjwiz-password-product" },
      h("strong", null, cjProduct?.title || t("h_cjwiz_title", "Synchronize product")),
      h("span", null, "CJ Dropshipping")
    )
  );

  // Version intégrée (editProduct.jsx) : mêmes classes cjwiz-modal__* que le
  // modal réel, plus les classes bootstrap modal-content/-header/-body/-footer
  // elles-mêmes (feuille de style globale react-bootstrap déjà chargée par
  // l'app, fonctionne hors de tout vrai <Modal>) -- garantit un rendu
  // strictement identique sans dupliquer une seule règle CSS. cjwiz-modal--inline
  // neutralise juste le `position:sticky` du footer (pensé pour un viewport
  // de modal scrollable, pas pour un flux de page normal).
  if (inline) {
    return h(React.Fragment, null,
      h("div", { className: "cjwiz-modal cjwiz-modal--inline" },
        h("div", { className: "modal-content" },
          h("div", { className: "modal-header cjwiz-modal__header" }, headerContent),
          h("div", { className: "modal-body cjwiz-modal__body" }, bodyContent),
          h("div", { className: "modal-footer cjwiz-modal__footer" }, footerContent)
        )
      )
      , passwordModal
    );
  }

  return h(React.Fragment, null,
    h(Modal, { show, onHide, size: "xl", scrollable: true, dialogClassName: "cjwiz-modal", centered: true, backdrop: "static" },
      h(Modal.Header, { closeButton: true, className: "cjwiz-modal__header" }, headerContent),
      h(Modal.Body, { className: "cjwiz-modal__body" }, bodyContent),
      h(Modal.Footer, { className: "cjwiz-modal__footer" }, footerContent)
    ),
    passwordModal
  );
};

module.exports = { CJSyncModal, buildCategoriesIDPayload };
